import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Chip, Name, Table, Text } from '~/components/blocks';
import { ContactMethod } from '~/components/pages/Patient/AllPatientList/AllPatient/ContactMethod';
import { useMedicationFollowupSettingStatus } from '~/components/partials/PatientDetailDialog/use-medication-followup-setting-status';
import { DirectVisitorOnAllPatientListFragment } from '~/graphql/generated/graphql';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';
import { Label } from '~/utils/label';

import { CommentColumn } from './CommentColumn';
import { MessageSendAtColumn } from './MessageSendAtColumn';
import { MessageSendFromColumn } from './MessageSendFromColumn';
import { QuestionnaireSheetColumn } from './QuestionnaireSheetColumn';
import { useSamePatient } from './SamePatientModal/use-same_patient';

type Props = {
  directVisitor: DirectVisitorOnAllPatientListFragment;
};

export const DirectVisitor = React.memo(({ directVisitor }: Props) => {
  const setPatientDetailDialogState = useSetAtom(patientDetailDialogState);
  const now = new Date();
  const latestFollowupMessage =
    directVisitor.medicationFollowupMessages.nodes.find((m) => !m.isAutoReplyMessage) || null;

  // すでに送信されたメッセージの最新を取得する
  const latestSentFollowupMessage =
    directVisitor.medicationFollowupMessages.nodes.find(
      (m) => !m.isAutoReplyMessage && m.status === 'sent' && new Date(m.sendAt) < now,
    ) || null;
  const latestSentFollowupAutoReplyMessage =
    directVisitor.medicationFollowupMessages.nodes.find(
      (m) => m.isAutoReplyMessage && m.status === 'sent' && new Date(m.sendAt) < now,
    ) || null;

  const { statusManagement } = useMedicationFollowupSettingStatus();
  const medixsPatient = directVisitor.medixsPatient;
  const medicationFollowupStatus =
    medixsPatient?.medicationFollowupStatus || directVisitor.medicationFollowupStatus;
  const isFollowingUp = medicationFollowupStatus === 'in_progress';
  const phoneNumber = medixsPatient?.mobilePhone || directVisitor.mobilePhone;

  const { getSamePatientByGuestPatient } = useSamePatient(directVisitor.id);
  const handleClick = useCallback(async () => {
    if (medixsPatient) {
      const guestPatient = medixsPatient.guestPatients[0];
      setPatientDetailDialogState((state) => ({
        ...state,
        isOpen: true,
        type: 'message' as TabType,
        patientId: guestPatient.id,
      }));
    } else {
      await getSamePatientByGuestPatient();
    }
  }, [getSamePatientByGuestPatient, medixsPatient, setPatientDetailDialogState]);

  const hasLineVisitor = medixsPatient?.guestPatients.some(
    (guestPatient) => guestPatient.__typename === 'LineVisitor' && guestPatient.active,
  );

  return (
    <Table.Tr onClick={handleClick}>
      <Table.Td>
        <Name
          familyName={directVisitor.familyName}
          givenName={directVisitor.givenName}
          phoneticFamilyName={directVisitor.phoneticFamilyName}
          phoneticGivenName={directVisitor.phoneticGivenName}
        />
      </Table.Td>
      <Table.Td>
        <Text size="m" whiteSpace="nowrap">
          {Label.age(directVisitor.birthDate)}({Label.sex(directVisitor.sex)})
        </Text>
      </Table.Td>
      <Table.Td>
        <ContactMethod
          hasApp={!!directVisitor.medixsPatient?.patient}
          phoneNumber={phoneNumber}
          hasLine={hasLineVisitor}
        />
      </Table.Td>
      <Table.Td>
        <MessageSendAtColumn latestFollowupMessage={latestFollowupMessage} />
      </Table.Td>
      <Table.Td>
        {statusManagement && isFollowingUp && (
          <Chip size="fixed" color="blue" textColor="white">
            対応中
          </Chip>
        )}
      </Table.Td>
      <Table.Td>
        <QuestionnaireSheetColumn
          latestSentFollowupMessage={latestSentFollowupMessage}
          latestSentFollowupAutoReplyMessage={latestSentFollowupAutoReplyMessage}
        />
      </Table.Td>
      <Table.Td>
        <CommentColumn patient={directVisitor} />
      </Table.Td>
      <Table.Td>
        <MessageSendFromColumn latestFollowupMessage={latestFollowupMessage} />
      </Table.Td>
    </Table.Tr>
  );
});

DirectVisitor.displayName = 'DirectVisitor';
